import { ValueByLocationStats } from '@kcftech/prometheustypescriptapiclient';
import { CHART_COLORS } from 'pages/Dashboard/colors';
import { Card, CardContent, CardTitle } from 'pages/Dashboard/components/styled';
import { VerticalScrollableBarChart } from 'pages/Dashboard/components/VerticalScrollableBarChart';
import { memo, useMemo } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    Legend,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { RenderableText } from 'recharts/types/component/Text';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { round } from 'utils/number';

export type ValueByLocationCardProps = Readonly<{
    data: ValueByLocationStats;
}>;

const StyledBarChart = styled(BarChart)`
    text {
        fill: ${tokens.text.primary};
    }
`;

type TooltipPayload = {
    payload?: {
        downtimeHoursActual: number;
        totalSavingsActual: number;
        unitsSavedActual: number;
    };
};

const tooltipFormatter = (
    value: number | undefined,
    name: string | undefined,
    props: TooltipPayload
) => {
    // Get actual unscaled values from the chart data entry
    const entry = props.payload;
    if (!entry) {
        return 'Unknown (error)';
    }
    if (name === 'Total Savings ($)') {
        return `$${round(entry.totalSavingsActual, 2).toLocaleString()}`;
    }
    if (name === 'Downtime Hours') {
        return `${round(entry.downtimeHoursActual, 1).toLocaleString()} hrs`;
    }
    if (name === 'Units Saved') {
        return `${round(entry.unitsSavedActual, 1).toLocaleString()}`;
    }
    return value != null ? round(value, 1).toLocaleString() : '';
};

function labelListFormatter(
    node: RenderableText,
    wrapper?: (v: string) => string
): RenderableText {
    const val = typeof node === 'number' ? round(node, 0).toLocaleString() : node;
    return wrapper && typeof val === 'string' ? wrapper(val) : val;
}

export const ValueByLocationCard = memo(function ValueByLocationCard({
    data,
}: ValueByLocationCardProps) {
    const chartData = useMemo(() => {
        // Find max values for normalization
        const maxDowntime = Math.max(
            ...data.LocationValues.map((l) => l.DowntimeHours),
            1
        );
        const maxSavings = Math.max(...data.LocationValues.map((l) => l.TotalSavings), 1);
        const maxUnits = Math.max(...data.LocationValues.map((l) => l.UnitsSaved), 1);

        return data.LocationValues.map((location) => ({
            name: location.LocationName,
            // Normalized values (0-100 scale for visual comparison)
            'Downtime Hours': (location.DowntimeHours / maxDowntime) * 100,
            'Total Savings ($)': (location.TotalSavings / maxSavings) * 100,
            'Units Saved': (location.UnitsSaved / maxUnits) * 100,
            // Original values for labels
            downtimeHoursActual: location.DowntimeHours,
            totalSavingsActual: location.TotalSavings,
            unitsSavedActual: location.UnitsSaved,
        }));
    }, [data.LocationValues]);

    return (
        <Card style={{ gridArea: 'value-by-location' }}>
            <CardTitle>Value by Location</CardTitle>
            <CardContent $contentPositioning="stretch">
                {chartData.length === 0 ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                        }}
                    >
                        <p>No data available for the selected filters</p>
                    </div>
                ) : (
                    <VerticalScrollableBarChart
                        xAxisHeight={30}
                        dataLength={chartData.length}
                        minBarHeight="12%"
                    >
                        {({ xAxisHeight }) => (
                            <StyledBarChart
                                data={chartData}
                                layout="vertical"
                                margin={{ top: 15, right: 15, left: 15, bottom: 5 }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke={tokens.border.default}
                                    horizontal={false}
                                />
                                <XAxis
                                    type="number"
                                    hide
                                    domain={[0, 120]}
                                    height={xAxisHeight}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={150}
                                    tick={{ fill: tokens.text.primary }}
                                    fontSize={14}
                                />
                                <Tooltip<number, string>
                                    formatter={tooltipFormatter}
                                    contentStyle={{
                                        backgroundColor: tokens.background.default,
                                        border: `1px solid ${tokens.border.default}`,
                                    }}
                                    labelStyle={{ color: tokens.text.primary }}
                                />
                                <Legend />
                                <Bar
                                    dataKey="Downtime Hours"
                                    fill={CHART_COLORS.blue}
                                    radius={[0, 5, 5, 0]}
                                >
                                    <LabelList
                                        dataKey="downtimeHoursActual"
                                        position="right"
                                        formatter={(v) =>
                                            labelListFormatter(v, (v) => `${v} hrs`)
                                        }
                                    />
                                </Bar>
                                <Bar
                                    dataKey="Total Savings ($)"
                                    fill={CHART_COLORS.green}
                                    radius={[0, 5, 5, 0]}
                                >
                                    <LabelList
                                        dataKey="totalSavingsActual"
                                        position="right"
                                        formatter={(v) =>
                                            labelListFormatter(v, (v) => `$${v}`)
                                        }
                                    />
                                </Bar>
                                <Bar
                                    dataKey="Units Saved"
                                    fill={CHART_COLORS.yellow}
                                    radius={[0, 5, 5, 0]}
                                >
                                    <LabelList
                                        dataKey="unitsSavedActual"
                                        position="right"
                                        formatter={(v) => labelListFormatter(v)}
                                    />
                                </Bar>
                            </StyledBarChart>
                        )}
                    </VerticalScrollableBarChart>
                )}
            </CardContent>
        </Card>
    );
});
